import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Card } from '../../components/ui';
import { isIos } from '../../lib/device';
import { LANGS, useLang } from '../../i18n';
import { useAuth } from '../../stores/auth';
import { GUIDE, GUIDE_IMAGES, type GuideBlock, type GuideCopy } from './guide-content';
import { LegalLinks } from '../legal/LegalPages';

type Platform = 'ios' | 'android';

/** /guide — install and how-to-play guide, readable before signing in. `#id` opens a section. */
export function GuidePage() {
  const lang = useLang((s) => s.lang);
  const setLang = useLang((s) => s.setLang);
  const signedIn = !!useAuth((s) => s.token);
  const navigate = useNavigate();
  const { hash } = useLocation();
  const g = GUIDE[lang];
  const [platform, setPlatform] = useState<Platform>(() => (isIos() || !/Android/i.test(navigator.userAgent) ? 'ios' : 'android'));

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
  }, [hash]);

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate(signedIn ? '/' : '/login'));

  return (
    <div className="absolute inset-0 overflow-y-auto overscroll-y-contain" data-testid="guide">
      <main className="mx-auto max-w-lg px-4 pt-[calc(var(--safe-top)+16px)] pb-[calc(var(--safe-bottom)+32px)]">
        <div className="flex items-center justify-between">
          <button onClick={back} className="text-sm font-semibold text-ink-300">
            {g.back}
          </button>
          <span className="flex gap-3 text-xs">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} className={lang === l.code ? 'font-bold text-volt' : 'text-ink-400'}>
                {l.label}
              </button>
            ))}
          </span>
        </div>

        <header className="mt-4 flex items-center gap-3">
          <img src="/icons/icon-192.png" alt="" className="size-14 rounded-2xl" />
          <div>
            <h1 className="font-display text-3xl font-bold leading-tight">{g.title}</h1>
            <p className="text-sm text-ink-400">{g.subtitle}</p>
          </div>
        </header>

        <nav className="mt-5 flex flex-wrap gap-2" aria-label={g.contents}>
          {g.sections.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="rounded-full border border-ink-700 bg-ink-850 px-3 py-1.5 text-xs font-semibold text-ink-200">
              {s.icon} {s.title}
            </a>
          ))}
        </nav>

        {g.sections.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-4 pt-8" data-testid={`guide-${s.id}`}>
            <h2 className="font-display text-2xl font-bold">
              <span className="mr-2">{s.icon}</span>
              {s.title}
            </h2>
            {s.blocks.some((b) => b.platform) ? <PlatformTabs g={g} value={platform} onChange={setPlatform} /> : null}
            <div className="mt-3 space-y-3">
              {s.blocks
                .filter((b) => !b.platform || b.platform === platform)
                .map((b, i) => (
                  <Block key={i} b={b} g={g} />
                ))}
              {s.faq?.map((f) => (
                <details key={f.q} className="rounded-2xl border border-ink-700/80 bg-ink-850 px-4 py-3">
                  <summary className="cursor-pointer font-semibold">{f.q}</summary>
                  <p className="mt-2 text-sm text-ink-300">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 text-center">
          <Link to={signedIn ? '/' : '/login'} className="inline-block rounded-2xl bg-volt px-6 py-3 font-bold text-ink-950">
            {signedIn ? '🏃 Runner Wars' : g.signIn}
          </Link>
        </div>
        <LegalLinks className="mt-6" />
      </main>
    </div>
  );
}

function PlatformTabs({ g, value, onChange }: { g: GuideCopy; value: Platform; onChange: (p: Platform) => void }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-1 rounded-2xl bg-ink-850 p-1" role="tablist">
      {(['ios', 'android'] as const).map((p) => (
        <button
          key={p}
          role="tab"
          aria-selected={value === p}
          onClick={() => onChange(p)}
          className={`rounded-xl py-2 text-sm font-bold ${value === p ? 'bg-volt text-ink-950' : 'text-ink-300'}`}
          data-testid={`guide-tab-${p}`}
        >
          {p === 'ios' ? g.iphone : g.android}
        </button>
      ))}
    </div>
  );
}

function Block({ b, g }: { b: GuideBlock; g: GuideCopy }) {
  return (
    <Card className="space-y-3">
      <div id={b.id} className={b.id ? 'scroll-mt-4' : undefined}>
        {b.title ? <h3 className="font-semibold text-ink-100">{b.title}</h3> : null}
        {b.text ? <p className={`text-sm text-ink-300 ${b.title ? 'mt-1' : ''}`}>{b.text}</p> : null}
      </div>
      {b.steps?.length ? (
        <ol className="space-y-4">
          {b.steps.map((st, i) => (
            <li key={i} className="flex gap-3">
              <span className="grid size-7 shrink-0 place-items-center rounded-full bg-volt/15 text-sm font-bold text-volt">{i + 1}</span>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-sm text-ink-200">{st.text}</p>
                {st.img ? <Shot name={st.img} alt={st.text} soon={g.screenshotSoon} /> : null}
              </div>
            </li>
          ))}
        </ol>
      ) : null}
      {b.bullets?.length ? (
        <ul className="list-disc space-y-1.5 pl-5 text-sm text-ink-300">
          {b.bullets.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      ) : null}
      {b.tip ? <p className="rounded-xl border border-gold/30 bg-gold/10 px-3 py-2 text-sm text-gold">💡 {b.tip}</p> : null}
    </Card>
  );
}

/** A phone screenshot, or a drawn placeholder until the image is added to public/guide-shots. */
function Shot({ name, alt, soon }: { name: string; alt: string; soon: string }) {
  if (GUIDE_IMAGES.has(name)) {
    return <img src={`/guide-shots/${name}.jpg`} alt={alt} loading="lazy" className="w-56 max-w-full rounded-2xl border border-ink-700" />;
  }
  return (
    <div className="grid h-24 w-48 max-w-full place-items-center rounded-2xl border border-dashed border-ink-700 text-center text-[11px] text-ink-500" aria-hidden>
      <span>
        📱
        <br />
        {soon}
      </span>
    </div>
  );
}
